export function InfinityArrowIcon({
  size = 16,
  className = "",
}: {
  size?: number;
  className?: string;
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 22 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      {/* Right loop */}
      <path
        d="M 11,11 C 12.5,6.5 20,6.5 20,11 C 20,15.5 12.5,15.5 11,11"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
      {/* Left loop */}
      <path
        d="M 11,11 C 9.5,15.5 2,15.5 2,11 C 2,6.5 9.5,6.5 11,11"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        fill="none"
      />
      <polygon points="16,6.3 19,8 16,9.3" fill="currentColor" />
      <polygon points="6,12.7 3,14.4 6,15.7" fill="currentColor" />
    </svg>
  );
}

/**
 * The stork icon mark as an SVG image.
 * variant="dark"  → dark teal background (#1A3535), use on light surfaces
 * variant="light" → warm sand background (#E8DDCF), use on dark surfaces
 */
export function REKINDERLogo({
  className = "h-11 w-auto",
  alt = "Rekinder",
  variant = "dark",
}: {
  className?: string;
  alt?: string;
  variant?: "dark" | "light";
}) {
  const src =
    variant === "light"
      ? `${import.meta.env.BASE_URL}images/rekinder-logo-light.svg`
      : `${import.meta.env.BASE_URL}images/rekinder-logo-dark.svg`;
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      draggable={false}
    />
  );
}

/**
 * Full wordmark: stork icon <img> + "Rekinder" text span in a flex row.
 * variant="dark"  → dark stork mark + #1A3535 text, for light/sand surfaces
 * variant="light" → light stork mark + #E8DDCF text, for dark surfaces (#1A3535)
 * iconHeight is a Tailwind height class for the icon (default h-9 = 36px).
 */
export function WordmarkLogo({
  variant = "dark",
  iconHeight = "h-9",
  className = "",
}: {
  variant?: "dark" | "light";
  iconHeight?: string;
  className?: string;
}) {
  // Dark variant: rekinder-mark-nav.svg — circular #1A3535 bg + cream stork + orange beak.
  // Light variant: transparent cream stork paths for use on dark footer bg.
  const markSrc =
    variant === "light"
      ? `${import.meta.env.BASE_URL}images/rekinder-mark-transparent-light.svg`
      : `${import.meta.env.BASE_URL}images/rekinder-mark-nav.svg`;

  const textColor = variant === "light" ? "#E8DDCF" : "#1A3535";

  const fontSizeMap: Record<string, string> = {
    "h-6": "13px", "h-7": "15px", "h-8": "18px", "h-9": "22px",
    "h-10": "24px", "h-11": "26px", "h-12": "28px",
  };
  const fontSize = fontSizeMap[iconHeight] ?? "22px";

  return (
    <div
      className={`flex items-center gap-2 ${className}`}
      style={{ userSelect: "none" }}
    >
      <img
        src={markSrc}
        alt=""
        className={`${iconHeight} w-auto`}
        draggable={false}
      />
      <span
        style={{
          fontFamily: "'DM Sans Variable', sans-serif",
          fontWeight: 500,
          fontSize,
          letterSpacing: "-0.02em",
          color: textColor,
          lineHeight: 1,
        }}
      >
        Rekinder
      </span>
    </div>
  );
}

/**
 * Legacy text-only wordmark — kept for backwards compatibility.
 * Prefer WordmarkLogo for new usage.
 */
export function HeaderLogo() {
  return (
    <span
      style={{
        fontFamily: "'DM Sans Variable', sans-serif",
        fontWeight: 800,
        fontSize: 23,
        letterSpacing: "-0.03em",
        lineHeight: 1,
        userSelect: "none",
      }}
      aria-label="Rekinder"
    >
      <span style={{ color: "#2D7A78" }}>Re</span>
      <span style={{ color: "#174B49" }}>kinder</span>
    </span>
  );
}
